import classnames from 'classnames'
import * as React from 'react'

import {
  Table,
  TableAlign,
  TableBody,
  TableHead,
  TableHeaderSortable,
  TableRow,
  getDefaultSortFromUrl,
  useTableSortWithAutoupdate,
} from '../../../components/Table'
import { SortByShape } from '../../../hooks/use-sort-data'
import { SortDirection } from '../../../types'
import { CallKind, ColumnId, ColumnRendererProps, Option, RowKind, TableColumnDefinition } from '../types'
import { PriceRow } from './PriceRow'
import { OptionsTableCell } from './TableColumn'

function defaultColumnRenderer(column: TableColumnDefinition, props: ColumnRendererProps) {
  return column.renderColumn(props)
}

function getColumnValueGetter(kind: RowKind, ticker?: string | null, lastClose?: number | null) {
  return (column: TableColumnDefinition) => (row: Option) => {
    if (typeof column.getValueForSort === 'function') {
      return column.getValueForSort({ kind, lastClose, row } as CallKind)
    }

    return row[column.id as keyof typeof row] ?? null
  }
}

interface OptionsTableProps {
  kind?: RowKind
  ticker: string | null
  lastClose?: number | null
  lastTime?: number | null
  options: Option[]
  columns: TableColumnDefinition[]
  isPriceRowEnabled?: boolean

  /**
   * Sorting key (column id, with optional dash prefix for descending)
   */
  tableOrder: string

  /**
   * Whether headers should become sticky
   */
  isHeaderSticky?: boolean
  headerClass?: string

  renderColumn?: (column: TableColumnDefinition, props: ColumnRendererProps) => React.ReactNode

  onSortChange?: (
    column: TableColumnDefinition,
    sort: SortByShape<TableColumnDefinition>,
    changeSort: (newSort: TableColumnDefinition, direction?: SortDirection) => void
  ) => void
}

export function OptionsTable({
  kind = RowKind.Call,
  ticker,
  lastClose,
  lastTime,
  columns,
  options,
  isPriceRowEnabled = true,
  tableOrder,
  isHeaderSticky,
  headerClass,
  renderColumn = defaultColumnRenderer,
  onSortChange,
}: OptionsTableProps) {
  const { data, sort, changeSort } = useTableSortWithAutoupdate({
    data: options,
    columns,
    defaultSort: getDefaultSortFromUrl(columns, tableOrder),
    valueGetter: getColumnValueGetter(kind, ticker, lastClose),
    tableOrder,
  })
  /**
   * We display current price as a row between strike prices
   */
  const currentPriceRow = React.useMemo(() => {
    const isVisible = isPriceRowEnabled && sort.column.id === ColumnId.StrikePrice

    if (!isVisible || typeof lastClose !== 'number') return { isVisible: false, rowIndex: -1 }

    const rowIndex =
      sort.direction === SortDirection.Ascending
        ? data.findLastIndex((option) => option.strike < lastClose)
        : data.findLastIndex((option) => option.strike > lastClose)

    return { isVisible: true, index: rowIndex }
  }, [isPriceRowEnabled, sort.column.id, sort.direction, lastClose, data])

  const priceRowProps = React.useMemo(
    () => ({
      lastClose: lastClose ?? null,
      lastTime: lastTime ?? null,
      leftSpan: 2,
      rightSpan: 7,
      showAdditionalCells: false,
      priceAlign: TableAlign.Right,
    }),
    [lastClose, lastTime]
  )

  return (
    <Table className="table-fixed [overflow-anchor:none]" cellPadding={1}>
      <TableHead className={classnames(headerClass, { 'sticky z-20 bg-primary': isHeaderSticky })}>
        {columns.map((column) => (
          <TableHeaderSortable
            key={column.id}
            align={column.align}
            className={classnames(
              column.className,
              'border-transparent first:border-l first:pl-2.5 last:border-r last:pr-2.5'
            )}
            isActive={column.id === sort.column.id}
            direction={sort.direction}
            onClick={() =>
              typeof onSortChange === 'function' ? onSortChange(column, sort, changeSort) : changeSort(column)
            }
          >
            {column.name}
          </TableHeaderSortable>
        ))}
      </TableHead>

      <TableBody>
        {data.map((option, rowIndex) => {
          const key = `${option.askPrice}-${option.exDate}-${option.strike}-${rowIndex}`
          const isHighlighted =
            typeof lastClose === 'number' &&
            (kind === RowKind.Call ? option.strike < lastClose : option.strike > lastClose)
          const isCurrentPriceRow = currentPriceRow.isVisible && currentPriceRow.index === rowIndex
          const showPriceAfter = isCurrentPriceRow && currentPriceRow.index < data.length - 1

          const row = (
            <TableRow key={key} className="styled-row is-hover-borders">
              {columns.map((column, index) => (
                <OptionsTableCell
                  key={column.id}
                  isFirst={index === 0}
                  isLast={index === columns.length - 1}
                  align={column.align}
                  isHighlighted={index === 0 && isHighlighted}
                  className={classnames({ 'border-b': showPriceAfter })}
                >
                  {renderColumn(column, { kind: kind, ticker, lastClose, row: option } as ColumnRendererProps)}
                </OptionsTableCell>
              ))}
            </TableRow>
          )

          if (showPriceAfter) {
            return (
              <React.Fragment key={key}>
                {row}
                {showPriceAfter && <PriceRow {...priceRowProps} />}
              </React.Fragment>
            )
          }

          return row
        })}
      </TableBody>
    </Table>
  )
}
